// src/pages/LearnYourNotes.jsx
// Piano With Pan V1.5 — Learn Your Notes Part page
// ─────────────────────────────────────────────────────────────────
// CHANGE LOG vs V1.4:
//  • Simplified layout — stats cards dropped, focus is just on the prompt
//    squares + piano. Calmer screen for young kids.
//  • Equal-size coloured prompt squares (note row reads left to right like
//    notation; same colour palette as the keys: C red, D yellow, E green,
//    F blue, G violet, A pink, B orange).
//  • AI voice narration via Web Speech API (female English voice):
//      - On every new prompt: "Find the note C and play it" / "Now play C, then D"
//      - On each correct sub-note: short praise ("Yes!", "Nice!")
//      - On a fully-completed prompt: "Excellent!" / "Amazing!"
//      - On wrong note: same line as the bubble, spoken aloud
//      - On part complete: "Wonderful! You finished CDE. Let's try F & G next!"
//    Voice toggle (🔊/🔇) in the header, persisted to localStorage.
//  • "Hear it" button keeps the piano-tone preview (on demand only, no
//    auto-play of the pitch).
//  • Skip to Level 1 button removed.
//  • Rainbow bouncing ball + speech bubble preserved over the expected key.

import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import RealPianoListener from "../components/RealPianoListener";
import VirtualPiano, {
  normalizeNoteName as vpNormalizeNoteName,
} from "../components/VirtualPiano";
import { sendGAEvent } from "../analytics/analytics";
import {
  speak, cancelSpeech,
  isVoiceEnabled, setVoiceEnabled, onVoiceEnabledChange,
  buildPromptIntro, praiseSubNote, praisePromptComplete,
  buildWrongFeedback, buildPartCompleteLine,
} from "../utils/voicePrompts";

/* ═══════════════════════════════════════════════
   COMPLETION FLAGS
═══════════════════════════════════════════════ */
function setTutorialDone() {
  try { localStorage.setItem("pwp_tutorial_done_v1", "1"); } catch {}
}
function markPartDone(id) {
  try { localStorage.setItem(`pwp_lyn_part_${id}_done_v1`, "1"); } catch {}
}

/* ═══════════════════════════════════════════════
   ASSETS / VIDEO
═══════════════════════════════════════════════ */
const LEARN_VIDEO_DEFAULT = "Learn Your Notes.mp4";
function buildVideoSources(filename) {
  return [
    "/videos/" + encodeURIComponent(filename),
    "/videos/" + filename,
    "/" + filename,
  ];
}

/* ═══════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════ */
const MIC_ID = "lyn-mic-ctrl";

// Same palette as PracticeMode / GameMenu / the side chips
const NOTE_COL = {
  C:{ bg:"#FEE2E2", text:"#991B1B", border:"#FCA5A5", glow:"rgba(239,68,68,0.32)"   },
  D:{ bg:"#FEF3C7", text:"#92400E", border:"#FCD34D", glow:"rgba(245,158,11,0.32)"  },
  E:{ bg:"#D1FAE5", text:"#065F46", border:"#6EE7B7", glow:"rgba(52,211,153,0.32)"  },
  F:{ bg:"#DBEAFE", text:"#1E40AF", border:"#93C5FD", glow:"rgba(96,165,250,0.32)"  },
  G:{ bg:"#EDE9FE", text:"#5B21B6", border:"#C4B5FD", glow:"rgba(167,139,250,0.32)" },
  A:{ bg:"#FCE7F3", text:"#9D174D", border:"#F9A8D4", glow:"rgba(244,114,182,0.32)" },
  B:{ bg:"#FFEDD5", text:"#9A3412", border:"#FDBA74", glow:"rgba(251,146,60,0.32)"  },
};

const MIC_CFG = {
  detectionSensitivity:1.25, toleranceCents:85, toleranceHz:14,
  wrongStableFrames:1, wrongCooldownMs:180, wrongCentsThreshold:70, wrongHzThreshold:7,
};

/* ═══════════════════════════════════════════════
   NOTE HELPERS
═══════════════════════════════════════════════ */
const FLAT_TO_SHARP = { DB:"C#",EB:"D#",GB:"F#",AB:"G#",BB:"A#",CB:"B",FB:"E" };
const EDGE_ENH      = { "E#":"F","B#":"C" };
const MIDI_PC       = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];
const NOTE_FREQ     = { C:261.63,D:293.66,E:329.63,F:349.23,G:392.0,A:440.0,B:493.88 };

function normSafe(n) {
  if (!n) return "";
  let r = String(n).trim().toUpperCase().replaceAll("♯","#").replaceAll("♭","B");
  const m = r.match(/[A-G](#|B)?/);
  if (!m) return "";
  let pc = m[0];
  if (EDGE_ENH[pc]) pc = EDGE_ENH[pc];
  if (pc.length===2 && pc[1]==="B") pc = FLAT_TO_SHARP[pc] || pc;
  return vpNormalizeNoteName ? vpNormalizeNoteName(pc) : pc;
}
function normAny(x) {
  if (x==null) return "";
  if (Array.isArray(x)) return normAny(x[x.length-1] ?? x[0]);
  if (typeof x==="number" && Number.isFinite(x)) return normSafe(MIDI_PC[((Math.round(x)%12)+12)%12]);
  if (typeof x==="object" && x.name) return normSafe(x.name);
  return normSafe(String(x).split(/[/,|]/)[0]);
}
function previewNote(n) {
  try {
    const freq = NOTE_FREQ[normSafe(n)]; if (!freq) return;
    const ac = new (window.AudioContext||window.webkitAudioContext)();
    const osc = ac.createOscillator(), g = ac.createGain();
    osc.type = "triangle"; osc.frequency.value = freq;
    g.gain.setValueAtTime(0.38, ac.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime+1.1);
    osc.connect(g).connect(ac.destination); osc.start(); osc.stop(ac.currentTime+1.1);
  } catch {}
}

/* ═══════════════════════════════════════════════
   PART CONFIGURATION
═══════════════════════════════════════════════ */
const PART_CONFIG = {
  a: {
    id: "a",
    title: "CDE",
    spokenTitle: "C D E",
    headerLabel: "🎹 Learn — CDE",
    notes: ["C","D","E"],
    prompts: [
      { id:1, label:"Note C",         sequence:["C"] },
      { id:2, label:"C → D",          sequence:["C","D"] },
      { id:3, label:"C → D → E",      sequence:["C","D","E"] },
      { id:4, label:"D twice",        sequence:["D","D"] },
      { id:5, label:"D → E",          sequence:["D","E"] },
      { id:6, label:"C → D → E → C",  sequence:["C","D","E","C"] },
      { id:7, label:"E E D D C",      sequence:["E","E","D","D","C"] },
    ],
    nextPartId: "b",
    nextSpokenTitle: "F and G",
    nextLabel:  "Continue to F & G →",
    videoFile:  LEARN_VIDEO_DEFAULT,
    pianoRange: { firstKey:"C4", lastKey:"C5" },
  },
  b: {
    id: "b",
    title: "F & G",
    spokenTitle: "F and G",
    headerLabel: "🎹 Learn — F & G",
    notes: ["F","G"],
    prompts: [
      { id:1, label:"Note F",         sequence:["F"] },
      { id:2, label:"Note G",         sequence:["G"] },
      { id:3, label:"F → G",          sequence:["F","G"] },
      { id:4, label:"G → F",          sequence:["G","F"] },
      { id:5, label:"F → G → F",      sequence:["F","G","F"] },
      { id:6, label:"G F G F",        sequence:["G","F","G","F"] },
      { id:7, label:"F G F G F",      sequence:["F","G","F","G","F"] },
    ],
    nextPartId: "c",
    nextSpokenTitle: "Note A",
    nextLabel:  "Continue to Note A →",
    videoFile:  LEARN_VIDEO_DEFAULT,
    pianoRange: { firstKey:"C4", lastKey:"C5" },
  },
  c: {
    id: "c",
    title: "Note A",
    spokenTitle: "Note A",
    headerLabel: "🎹 Learn — Note A + Twinkle",
    notes: ["A"],
    prompts: [
      { id:1, label:"Note A",                  sequence:["A"] },
      { id:2, label:"G → A",                   sequence:["G","A"] },
      { id:3, label:"A → A",                   sequence:["A","A"] },
      { id:4, label:"C → C (Twinkle…)",        sequence:["C","C"] },
      { id:5, label:"C C G G",                 sequence:["C","C","G","G"] },
      { id:6, label:"A A G",                   sequence:["A","A","G"] },
      { id:7, label:"C C G G A A G (full!)",   sequence:["C","C","G","G","A","A","G"] },
    ],
    nextPartId: null,
    nextSpokenTitle: null,
    nextLabel:  null,
    videoFile:  LEARN_VIDEO_DEFAULT,
    pianoRange: { firstKey:"C4", lastKey:"C5" },
  },
};

/* ═══════════════════════════════════════════════
   PROMPT FLATTENING
═══════════════════════════════════════════════ */
function flattenPrompts(prompts) {
  const flat = [];
  for (const p of prompts) for (const n of p.sequence) flat.push(n);
  return flat;
}
function deriveStepState(matched, prompts) {
  let rem = matched;
  for (let i=0; i<prompts.length; i++) {
    const len = prompts[i].sequence.length;
    if (rem < len) return { promptIndex:i, subIndex:rem };
    rem -= len;
  }
  return { promptIndex: prompts.length, subIndex: 0 };
}
function getExpectedAt(matched, flat) {
  if (matched >= flat.length) return null;
  return flat[matched];
}

/* ═══════════════════════════════════════════════
   BUBBLE TEXT (matches the spoken line)
═══════════════════════════════════════════════ */
function bubbleTextForWrong(played, expected) {
  return buildWrongFeedback(played, expected); // same wording as voice
}

/* ═══════════════════════════════════════════════
   BOUNCING RAINBOW BALL + SPEECH BUBBLE
═══════════════════════════════════════════════ */
function BouncingBall({ targetNote, wrapperRef, message, mood }) {
  const [pos, setPos] = useState(null);

  const recalc = useCallback(() => {
    if (!targetNote || !wrapperRef?.current) { setPos(null); return; }
    const wrap = wrapperRef.current, wR = wrap.getBoundingClientRect();
    let el = wrap.querySelector(`[data-note="${targetNote}"]`);
    if (!el) {
      const pc = normAny(targetNote);
      if (pc) el = wrap.querySelector(`[data-note^="${pc}"]`);
    }
    if (!el) { setPos(null); return; }
    const kR = el.getBoundingClientRect();
    setPos({ cx: kR.left - wR.left + kR.width/2, cy: kR.top - wR.top - 58 });
  }, [targetNote, wrapperRef]);

  useEffect(() => {
    recalc();
    const t1 = setTimeout(recalc, 100);
    const t2 = setTimeout(recalc, 400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [recalc]);

  useEffect(() => {
    const fn = () => setTimeout(recalc, 100);
    window.addEventListener("resize", fn);
    window.addEventListener("scroll", fn, { passive:true });
    return () => {
      window.removeEventListener("resize", fn);
      window.removeEventListener("scroll", fn);
    };
  }, [recalc]);

  if (!pos) return null;
  const D = 38;

  const bubbleBg     = mood === "wrong" ? "#FEE2E2" : mood === "happy" ? "#DCFCE7" : "#FFFFFF";
  const bubbleBorder = mood === "wrong" ? "#FCA5A5" : mood === "happy" ? "#86EFAC" : "#E2E8F0";
  const bubbleText   = mood === "wrong" ? "#991B1B" : mood === "happy" ? "#166534" : "#1E293B";

  return (
    <>
      <div
        style={{
          position:"absolute", left:pos.cx - D/2, top:pos.cy, width:D, height:D,
          borderRadius:"50%",
          background:"conic-gradient(from 0deg,#FF6B6B,#FFE66D,#6BCB77,#4D96FF,#FF6B6B)",
          boxShadow:"0 6px 18px rgba(0,0,0,0.22),inset 0 -3px 6px rgba(0,0,0,0.12),inset 0 3px 8px rgba(255,255,255,0.45)",
          animation: mood === "wrong"
            ? "lynBallShake 0.35s ease"
            : "lynBallBounce 0.52s ease-in-out infinite alternate",
          pointerEvents:"none", zIndex:30,
        }}
      >
        <div style={{
          position:"absolute", top:5, left:8, width:10, height:6, borderRadius:"50%",
          background:"rgba(255,255,255,0.68)", transform:"rotate(-35deg)"
        }}/>
      </div>

      {message && (
        <div
          style={{
            position:"absolute", left:pos.cx, top:pos.cy - 12,
            transform:"translate(-50%, -100%)",
            background:bubbleBg, border:`2px solid ${bubbleBorder}`, color:bubbleText,
            padding:"7px 12px", borderRadius:14,
            fontFamily:"'Nunito',sans-serif", fontWeight:800, fontSize:"0.78rem",
            lineHeight:1.25, maxWidth:240, minWidth:80, textAlign:"center",
            boxShadow:"0 4px 14px rgba(0,0,0,0.12)", zIndex:31, pointerEvents:"none",
            animation:"lynBubblePop 0.25s cubic-bezier(.34,1.56,.64,1)",
            whiteSpace:"normal", wordBreak:"break-word",
          }}
        >
          {message}
          <div style={{
            position:"absolute", bottom:-7, left:"50%", transform:"translateX(-50%)",
            width:0, height:0,
            borderLeft:"7px solid transparent", borderRight:"7px solid transparent",
            borderTop:`7px solid ${bubbleBorder}`,
          }}/>
          <div style={{
            position:"absolute", bottom:-4, left:"50%", transform:"translateX(-50%)",
            width:0, height:0,
            borderLeft:"5px solid transparent", borderRight:"5px solid transparent",
            borderTop:`5px solid ${bubbleBg}`,
          }}/>
        </div>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════
   PROMPT SQUARES — equal-size coloured tiles, left to right
═══════════════════════════════════════════════ */
function PromptSquares({ sequence, subIndex, flashState, onHear }) {
  const isWrong = flashState === "wrong";

  return (
    <div className="lyn-prompt-card">
      <div className="lyn-prompt-label">
        {flashState === "correct" ? "Correct!" :
         flashState === "wrong"   ? "Try again" :
         "Find this note"}
      </div>

      <div className="lyn-squares-row">
        {sequence.map((n, i) => {
          const done = i < subIndex;
          const cur  = i === subIndex;
          const c    = NOTE_COL[n] || NOTE_COL.C;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onHear(n)}
              className={`lyn-square ${done ? "lyn-square-done" : cur ? "lyn-square-current" : "lyn-square-upcoming"} ${cur && isWrong ? "lyn-square-shake" : ""}`}
              style={{
                background: done ? "#DCFCE7" : c.bg,
                borderColor: done ? "#86EFAC" : c.border,
                color: done ? "#16A34A" : c.text,
                boxShadow: cur
                  ? `0 8px 0 ${c.border}, 0 12px 24px ${c.glow}`
                  : done
                  ? "0 4px 0 #86EFAC, 0 6px 14px rgba(34,197,94,0.25)"
                  : `0 4px 0 ${c.border}, 0 6px 14px ${c.glow}`,
              }}
              aria-label={`Note ${n}${done ? " — done" : cur ? " — play next" : ""}`}
            >
              <span className="lyn-square-letter">{done ? "✓" : n}</span>
              {cur && <span className="lyn-square-ring" aria-hidden="true" />}
            </button>
          );
        })}
      </div>

      <button type="button" className="lyn-hear-btn" onClick={() => onHear(sequence[Math.min(subIndex, sequence.length-1)])}>
        🔊 Hear it
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   FINISH MODAL
═══════════════════════════════════════════════ */
function FinishModal({ open, part, onClose, onNext, onMenu, onAgain, onPractice }) {
  if (!open || !part) return null;
  const isLast = !part.nextPartId;
  const labels = { a:"CDE", b:"F&G", c:"Note A" };
  const order  = { a:0, b:1, c:2 };

  return (
    <div className="lyn-modal-overlay">
      <div className="lyn-modal">
        <button type="button" className="lyn-modal-close" onClick={onClose}>✕</button>

        <div className="lyn-modal-emoji">🎉</div>
        <div className="lyn-modal-title">{part.title} Complete!</div>
        <div className="lyn-modal-sub">
          You learned {part.notes.join(", ")}{isLast ? " — and the first line of Twinkle Twinkle!" : "."}
        </div>

        {/* Journey strip */}
        <div className="lyn-modal-journey">
          {["a","b","c"].map((id) => {
            const done   = order[part.id] > order[id];
            const active = part.id === id;
            return (
              <span
                key={id}
                className={`lyn-modal-journey-pill ${active ? "lyn-modal-journey-pill-active" : done ? "lyn-modal-journey-pill-done" : ""}`}
              >
                {done ? "✓ " : ""}{labels[id]}
              </span>
            );
          })}
        </div>

        <div className="lyn-modal-actions">
          {isLast ? (
            <button type="button" className="lyn-modal-primary" onClick={onPractice}>
              🐉 Try Practice Mode
            </button>
          ) : (
            <button type="button" className="lyn-modal-next" onClick={onNext}>
              {part.nextLabel}
            </button>
          )}

          <div className="lyn-modal-secondary-row">
            <button type="button" className="lyn-modal-secondary" onClick={onAgain}>
              Repeat {part.title}
            </button>
            <button type="button" className="lyn-modal-tertiary" onClick={onMenu}>
              Back to Menu
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════ */
export default function LearnYourNotes() {
  const go = useNavigate();
  const { partId: rawPartId } = useParams();
  const partId = (rawPartId || "").toLowerCase();
  const partRaw = PART_CONFIG[partId];
  const part = partRaw || PART_CONFIG.a;

  /* Window width */
  const [wW, setWW] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  useEffect(() => {
    let raf = 0;
    const fn = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => setWW(window.innerWidth)); };
    window.addEventListener("resize", fn);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", fn); };
  }, []);
  const isTablet  = wW >= 640 && wW < 1024;
  const isDesktop = wW >= 1024;

  /* Piano sizing */
  const pianoCfg = useMemo(() => {
    if (isDesktop) return { kw:64, wh:210, bkw:38, bkh:132 };
    if (isTablet) {
      const kw = Math.min(Math.floor((wW - 80) / 8), 60);
      const r = kw / 60;
      return { kw, wh:Math.round(200*r), bkw:Math.round(36*r), bkh:Math.round(126*r) };
    }
    const kw = Math.floor((wW - 24) / 8);
    const r = kw / 40;
    return { kw, wh:Math.round(148*r), bkw:Math.round(26*r), bkh:Math.round(94*r) };
  }, [isDesktop, isTablet, wW]);

  /* Prompts → flat melody */
  const PROMPTS     = part.prompts;
  const FLAT_TARGET = useMemo(() => flattenPrompts(PROMPTS), [PROMPTS]);
  const TOTAL_NOTES = FLAT_TARGET.length;

  /* Single global counter */
  const [globalMatched, setGlobalMatched] = useState(0);
  const allDone = globalMatched >= TOTAL_NOTES;

  const { promptIndex, subIndex } = useMemo(
    () => deriveStepState(globalMatched, PROMPTS),
    [globalMatched, PROMPTS]
  );
  const curPrompt = PROMPTS[Math.min(promptIndex, PROMPTS.length-1)];
  const expected  = !allDone ? getExpectedAt(globalMatched, FLAT_TARGET) : null;

  /* UI state */
  const [flash,     setFlash    ] = useState("idle");
  const [started,   setStarted  ] = useState(false);
  const [listening, setListening] = useState(false);
  const [showEnd,   setShowEnd  ] = useState(false);
  const [toast,     setToast    ] = useState("");

  /* Speech bubble */
  const [bubbleMsg,  setBubbleMsg ] = useState(null);
  const [bubbleMood, setBubbleMood] = useState("idle");
  const bubbleTimerRef = useRef(null);
  const showBubble = useCallback((msg, mood = "idle", ms = 2400) => {
    if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current);
    setBubbleMsg(msg);
    setBubbleMood(mood);
    bubbleTimerRef.current = setTimeout(() => {
      setBubbleMsg(null);
      setBubbleMood("idle");
    }, ms);
  }, []);
  useEffect(() => () => { if (bubbleTimerRef.current) clearTimeout(bubbleTimerRef.current); }, []);

  const flashT    = useRef(null);
  const toastT    = useRef(null);
  const wrongGate = useRef({ at:0, pc:null, n:0 });
  useEffect(() => () => {
    if (flashT.current) clearTimeout(flashT.current);
    if (toastT.current) clearTimeout(toastT.current);
  }, []);

  /* Voice on/off (header toggle, syncs with localStorage) */
  const [voiceOn, setVoiceOn] = useState(() => isVoiceEnabled());
  useEffect(() => onVoiceEnabledChange(setVoiceOn), []);
  const toggleVoice = useCallback(() => {
    setVoiceEnabled(!voiceOn);
    sendGAEvent("learn_notes_voice_toggle", { part_id: partId, enabled: !voiceOn });
  }, [voiceOn, partId]);

  /* Video state */
  const VIDEO_SOURCES = useMemo(() => buildVideoSources(part.videoFile), [part.videoFile]);
  const [showLearnVideo, setShowLearnVideo] = useState(true);
  const [learnSrcIdx,    setLearnSrcIdx   ] = useState(0);
  const [learnFailed,    setLearnFailed   ] = useState(false);
  const handleLearnError = () => {
    setLearnSrcIdx(i => {
      if (i + 1 < VIDEO_SOURCES.length) return i + 1;
      setLearnFailed(true);
      return i;
    });
  };

  /* Reset everything on partId change */
  useEffect(() => {
    cancelSpeech();
    setGlobalMatched(0);
    setFlash("idle"); setStarted(false); setListening(false);
    setShowEnd(false); setToast("");
    setBubbleMsg(null); setBubbleMood("idle");
    wrongGate.current = { at:0, pc:null, n:0 };
    setShowLearnVideo(true); setLearnSrcIdx(0); setLearnFailed(false);
    sendGAEvent("learn_notes_part_view", { part_id: partId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partId]);

  /* Cancel any in-flight speech on unmount */
  useEffect(() => () => { cancelSpeech(); }, []);

  /* ── Voice: speak the prompt intro whenever the active prompt changes
        (but not while the intro video overlay is on; we don't want two voices) */
  const lastSpokenPromptRef = useRef(-1);
  useEffect(() => {
    if (allDone) return;
    if (showLearnVideo) return;        // wait until the video is dismissed
    if (lastSpokenPromptRef.current === promptIndex) return;
    lastSpokenPromptRef.current = promptIndex;
    const seq = curPrompt?.sequence || [];
    if (!seq.length) return;
    // Tiny delay so the screen renders first
    const t = setTimeout(() => {
      speak(buildPromptIntro(seq), { priority: true });
    }, 250);
    return () => clearTimeout(t);
  }, [promptIndex, curPrompt, allDone, showLearnVideo]);

  /* Finish detection */
  useEffect(() => {
    if (allDone && !showEnd) {
      try { const b = document.querySelector(`#${MIC_ID} button`); if (b) b.click(); } catch {}
      setListening(false);
      markPartDone(part.id);
      if (part.id === "a") setTutorialDone();
      sendGAEvent("learn_notes_complete", { part_id: part.id, total_notes: TOTAL_NOTES });
      // Spoken: "Wonderful! You finished CDE. Let's try F and G next!"
      speak(buildPartCompleteLine(part.spokenTitle, part.nextSpokenTitle), { priority: true });
      // Show modal a beat later so the line gets some space
      const t = setTimeout(() => setShowEnd(true), 800);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDone]);

  /* Helpers */
  const trigFlash = useCallback((kind) => {
    if (flashT.current) clearTimeout(flashT.current);
    setFlash(kind);
    flashT.current = setTimeout(() => setFlash("idle"), kind === "correct" ? 500 : 290);
  }, []);
  const showToast = useCallback((msg) => {
    setToast(msg);
    if (toastT.current) clearTimeout(toastT.current);
    toastT.current = setTimeout(() => setToast(""), 2200);
  }, []);

  /* Key glow */
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.querySelectorAll(".tt-current-key-glow").forEach(el => el.classList.remove("tt-current-key-glow"));
    if (!expected || allDone) return;
    let els = Array.from(document.querySelectorAll(`[data-note="${expected}"]`));
    if (!els.length) {
      const pc = normAny(expected);
      if (pc) els = Array.from(document.querySelectorAll(`[data-note^="${pc}"]`));
    }
    els.forEach(el => el.classList.add("tt-current-key-glow"));
  }, [expected, allDone, promptIndex, subIndex]);

  /* ── Correct / wrong handlers (shared by virtual + mic) ── */
  // We compute "is this the last sub-note of the current prompt?" inside the
  // handler so the praise line is appropriate.
  const onCorrectAt = useCallback((globalIdx) => {
    trigFlash("correct");
    setGlobalMatched(prev => Math.min(TOTAL_NOTES, Math.max(prev, globalIdx + 1)));

    // Decide: was this the last sub-note of the prompt?
    const { promptIndex: pIdx, subIndex: sIdx } = deriveStepState(globalIdx, PROMPTS);
    const promptLen = PROMPTS[pIdx]?.sequence?.length || 1;
    const isFinalSubNote = sIdx === promptLen - 1;
    const isLastNoteOfPart = globalIdx + 1 >= TOTAL_NOTES;

    if (isLastNoteOfPart) {
      // Part-complete line is spoken by the finish effect — stay quiet here
      return;
    }
    if (isFinalSubNote) {
      speak(praisePromptComplete(), { priority: true });
    } else {
      speak(praiseSubNote());
    }
  }, [trigFlash, TOTAL_NOTES, PROMPTS]);

  const onWrongAt = useCallback((playedNote, expectedNote) => {
    trigFlash("wrong");
    if (playedNote && expectedNote) {
      const line = bubbleTextForWrong(playedNote, expectedNote);
      showBubble(line, "wrong", 2600);
      speak(line, { priority: true });
    }
  }, [trigFlash, showBubble]);

  /* Mic toggle */
  const toggleMic = useCallback(() => {
    const btn = document.querySelector(`#${MIC_ID} button`);
    if (btn) { btn.click(); setListening(p => !p); }
  }, []);

  /* Virtual piano click */
  const onKey = useCallback((noteName) => {
    if (allDone) return;
    if (!started) setStarted(true);
    if (listening) { toggleMic(); showToast("Switched to virtual piano — mic off"); }
    const played = normAny(noteName);
    const exp = expected ? normAny(expected) : "";
    if (!played || !exp) return;
    if (played === exp) {
      onCorrectAt(globalMatched);
    } else {
      onWrongAt(played, exp);
    }
  }, [allDone, started, listening, toggleMic, showToast, expected, onCorrectAt, onWrongAt, globalMatched]);

  /* Mic detected — wrong gate */
  const onMicDetected = useCallback((note) => {
    const pc = normAny(note);
    if (!pc || allDone || !listening) return;
    const exp = expected ? normAny(expected) : "";
    if (!exp || pc === exp) {
      wrongGate.current = { at:0, pc:null, n:0 };
      return;
    }
    const now = Date.now(), g = wrongGate.current;
    if (g.pc !== pc) { wrongGate.current = { at:now, pc, n:1 }; return; }
    const n = (g.n || 0) + 1;
    wrongGate.current = { at:now, pc, n };
    if (n >= 2 && now - g.at >= 220) {
      onWrongAt(pc, exp);
      wrongGate.current = { at:now, pc, n:0 };
    }
  }, [allDone, listening, expected, onWrongAt]);

  /* Mic matched (cumulative) */
  const onMicMatched = useCallback((arr) => {
    if (!Array.isArray(arr) || allDone) return;
    const len = arr.length;
    setGlobalMatched(prev => {
      if (len <= prev) return prev;
      // For each newly-matched note, decide praise
      for (let g = prev; g < len; g++) {
        const { promptIndex: pIdx, subIndex: sIdx } = deriveStepState(g, PROMPTS);
        const promptLen = PROMPTS[pIdx]?.sequence?.length || 1;
        const isFinalSubNote = sIdx === promptLen - 1;
        const isLastNoteOfPart = g + 1 >= TOTAL_NOTES;
        if (isLastNoteOfPart) {
          // part-complete handler will speak the wrap-up
        } else if (isFinalSubNote) {
          speak(praisePromptComplete(), { priority: true });
        } else {
          speak(praiseSubNote());
        }
      }
      trigFlash("correct");
      wrongGate.current = { at:0, pc:null, n:0 };
      return len;
    });
  }, [allDone, trigFlash, PROMPTS, TOTAL_NOTES]);

  /* Reset */
  const reset = useCallback(() => {
    cancelSpeech();
    if (listening) {
      try { const b = document.querySelector(`#${MIC_ID} button`); if (b) b.click(); } catch {}
    }
    setListening(false); setStarted(false); setShowEnd(false);
    setGlobalMatched(0);
    setFlash("idle"); setToast("");
    setBubbleMsg(null); setBubbleMood("idle");
    wrongGate.current = { at:0, pc:null, n:0 };
    lastSpokenPromptRef.current = -1; // re-trigger intro speech
    sendGAEvent("learn_notes_restart", { part_id: part.id });
  }, [listening, part.id]);

  /* Piano wrap ref */
  const pianoWrapRef = useRef(null);

  /* ══════════════════════════════════════════
     EARLY EXIT — after all hooks
  ══════════════════════════════════════════ */
  if (!partRaw) return <Navigate to="/learn-your-notes" replace />;

  /* ══════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════ */
  return (
    <div className="lyn-part-root">
      <style>{CSS}</style>

      {/* Background blobs */}
      <div className="lyn-bg">
        <div className="lyn-bg-base" />
        <div className="lyn-blob lyn-blob-1" />
        <div className="lyn-blob lyn-blob-2" />
      </div>

      {/* Header */}
      <header className="lyn-header">
        <button
          type="button"
          className="lyn-hdr-btn"
          onClick={() => { sendGAEvent("learn_notes_part_back", { part_id: partId }); go("/learn-your-notes"); }}
          aria-label="Back to all parts"
        >
          ←
        </button>
        <div className="lyn-hdr-title">
          <span>{part.headerLabel}</span>
        </div>
        <button
          type="button"
          className={`lyn-voice-btn ${voiceOn ? "lyn-voice-btn-on" : "lyn-voice-btn-off"}`}
          onClick={toggleVoice}
          aria-label={voiceOn ? "Mute voice" : "Unmute voice"}
          title={voiceOn ? "Voice on — tap to mute" : "Voice off — tap to unmute"}
        >
          {voiceOn ? "🔊" : "🔇"}
        </button>
      </header>

      {/* Toast */}
      {toast && (
        <div className="lyn-toast">{toast}</div>
      )}

      {/* MAIN */}
      <main className="lyn-part-main">

        {/* Progress dots — small, single row */}
        <div className="lyn-progress-row">
          <span className="lyn-progress-count">Prompt {Math.min(promptIndex+1, PROMPTS.length)} of {PROMPTS.length}</span>
          <div className="lyn-progress-dots">
            {Array.from({length: PROMPTS.length}, (_,i) => {
              const done = i < promptIndex;
              const cur  = i === promptIndex;
              return (
                <div key={i} className={`lyn-progress-dot ${done ? "lyn-progress-dot-done" : cur ? "lyn-progress-dot-cur" : ""}`}/>
              );
            })}
          </div>
        </div>

        {/* Prompt squares */}
        {!allDone && (
          <PromptSquares
            sequence={curPrompt?.sequence || ["C"]}
            subIndex={subIndex}
            flashState={flash}
            onHear={previewNote}
          />
        )}

        {/* Mic button */}
        {!allDone && (
          <div className="lyn-mic-row">
            {!started ? (
              <button type="button" className="lyn-mic-start" onClick={() => { setStarted(true); toggleMic(); }}>
                🎤 Use Real Piano
              </button>
            ) : (
              <button type="button" className={`lyn-mic-toggle ${listening ? "lyn-mic-toggle-on" : ""}`} onClick={toggleMic}>
                {listening ? "🎤 Listening…" : "🎤 Enable Mic"}
              </button>
            )}
          </div>
        )}

        {!allDone && (
          <div className="lyn-or-label">— or click a key —</div>
        )}

        {/* Piano */}
        <div ref={pianoWrapRef} className="lyn-piano-wrap">
          {expected && !allDone && (
            <BouncingBall
              targetNote={expected}
              wrapperRef={pianoWrapRef}
              message={bubbleMsg}
              mood={bubbleMood}
            />
          )}
          <VirtualPiano
            onNoteClick={onKey}
            keyWidth={pianoCfg.kw} whiteKeyHeight={pianoCfg.wh}
            blackKeyWidth={pianoCfg.bkw} blackKeyHeight={pianoCfg.bkh}
            firstKey={part.pianoRange.firstKey} lastKey={part.pianoRange.lastKey}
            showAllBlackLabels={false}
          />
        </div>

        {/* Bottom utility links */}
        <div className="lyn-utility-row">
          <button type="button" className="lyn-utility-link" onClick={reset}>Restart</button>
          <span className="lyn-utility-sep">·</span>
          <button type="button" className="lyn-utility-link" onClick={() => setShowLearnVideo(true)}>▶ Replay Video</button>
          <span className="lyn-utility-sep">·</span>
          <button type="button" className="lyn-utility-link" onClick={() => go("/learn-your-notes")}>← All Parts</button>
        </div>
      </main>

      {/* Hidden RealPianoListener */}
      <div id={MIC_ID} style={{display:"none"}}>
        <RealPianoListener
          targetMelody={FLAT_TARGET}
          startLabel=" " stopLabel=" "
          showMicHint={false}
          buttonWrapperClassName="hidden"
          containerClassName="hidden"
          onNoteDetected={onMicDetected}
          onWrongConfirmed={() => onWrongAt(null, expected)}
          onNoteMatched={onMicMatched}
          onComplete={() => setGlobalMatched(TOTAL_NOTES)}
          detectionSensitivity={MIC_CFG.detectionSensitivity}
          toleranceCents={MIC_CFG.toleranceCents}
          toleranceHz={MIC_CFG.toleranceHz}
          wrongStableFramesRequired={MIC_CFG.wrongStableFrames}
          wrongCooldownMs={MIC_CFG.wrongCooldownMs}
          wrongCentsThreshold={MIC_CFG.wrongCentsThreshold}
          wrongHzThreshold={MIC_CFG.wrongHzThreshold}
        />
      </div>

      {/* Stop listening sticky */}
      {listening && !allDone && (
        <div className="lyn-stop-listen-wrap">
          <button type="button" className="lyn-stop-listen-btn" onClick={toggleMic}>
            ⏹ Stop Listening
          </button>
        </div>
      )}

      {/* Intro video */}
      {showLearnVideo && (
        <div className="lyn-video-overlay">
          <div className="lyn-video-modal">
            <button
              type="button"
              className="lyn-video-close"
              onClick={() => {
                sendGAEvent("learn_notes_intro_video_skipped", { part_id: partId });
                setShowLearnVideo(false);
              }}
            >✕</button>
            <div className="lyn-video-title">{part.headerLabel}</div>
            {!learnFailed ? (
              <div className="lyn-video-frame">
                <video
                  src={VIDEO_SOURCES[learnSrcIdx]}
                  controls autoPlay playsInline
                  onPlay ={() => sendGAEvent("learn_notes_intro_video_play",  { part_id: partId })}
                  onEnded={() => { sendGAEvent("learn_notes_intro_video_ended", { part_id: partId }); setShowLearnVideo(false); }}
                  onError={handleLearnError}
                />
              </div>
            ) : (
              <div className="lyn-video-missing">
                Video for {part.title} not found. Place it at <code>/public/videos/{part.videoFile}</code>.
              </div>
            )}
            <div className="lyn-video-cta-row">
              <button type="button" className="lyn-video-cta" onClick={() => setShowLearnVideo(false)}>
                Let's Play! →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Finish modal */}
      <FinishModal
        open={showEnd}
        part={part}
        onClose={() => setShowEnd(false)}
        onNext={() => {
          sendGAEvent("learn_notes_next_part", { part_id: part.id, next: part.nextPartId });
          go(`/learn-your-notes/${part.nextPartId}`);
        }}
        onMenu={() => {
          sendGAEvent("learn_notes_back_to_menu", { part_id: part.id });
          go("/learn-your-notes");
        }}
        onAgain={reset}
        onPractice={() => {
          sendGAEvent("learn_notes_to_practice", { part_id: part.id });
          go("/practice");
        }}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@600;700;800;900&display=swap');

.lyn-part-root {
  min-height: 100dvh;
  font-family: 'Nunito', sans-serif;
  color: #0f172a;
  position: relative;
  display: flex; flex-direction: column;
}
.lyn-part-root * { box-sizing: border-box; }

.tt-current-key-glow {
  box-shadow: 0 0 0 4px rgba(59,130,246,0.50), 0 0 22px rgba(59,130,246,0.65) !important;
  transform: translateY(-2px); transition: box-shadow .15s ease, transform .15s ease;
  border-radius: 10px; z-index: 5 !important;
}

@keyframes lynBallBounce { from{transform:translateY(0) scale(1)} to{transform:translateY(-22px) scale(1.07)} }
@keyframes lynBallShake {
  0%,100%{transform:translateX(0)} 25%{transform:translateX(-6px)}
  50%{transform:translateX(6px)} 75%{transform:translateX(-3px)}
}
@keyframes lynBubblePop {
  from { transform: translate(-50%, -100%) scale(0.6); opacity: 0; }
  to   { transform: translate(-50%, -100%) scale(1);   opacity: 1; }
}
@keyframes lynFloat { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(15px,-20px) scale(1.04); } }
@keyframes lynSquareShake {
  0%,100%{transform:translateY(0)} 25%{transform:translate(-5px,0)}
  50%{transform:translate(5px,0)} 75%{transform:translate(-3px,0)}
}
@keyframes lynSquareRingPulse {
  0% { transform: scale(1); opacity: 0.6; }
  100% { transform: scale(1.18); opacity: 0; }
}

button { -webkit-tap-highlight-color: transparent; }

/* ── Background ── */
.lyn-bg { pointer-events: none; position: fixed; inset: 0; z-index: 0; overflow: hidden; }
.lyn-bg-base {
  position: absolute; inset: 0;
  background: linear-gradient(180deg, #fffbeb 0%, #ffffff 60%, #f5f3ff 100%);
}
.lyn-blob {
  position: absolute; border-radius: 50%; filter: blur(100px); opacity: 0.3;
  animation: lynFloat 20s infinite ease-in-out;
}
.lyn-blob-1 { top: -5%; left: -8%; width: 400px; height: 400px; background: linear-gradient(135deg, #fde68a, #fbbf24); }
.lyn-blob-2 { bottom: -10%; right: -5%; width: 360px; height: 360px; background: linear-gradient(135deg, #c4b5fd, #8b5cf6); animation-delay: 4s; }

/* ── Header ── */
.lyn-header {
  position: sticky; top: 0; z-index: 100;
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding: 10px 16px;
  background: rgba(255,255,255,0.92); backdrop-filter: blur(14px);
  border-bottom: 1px solid #f1f5f9;
}
.lyn-hdr-btn {
  width: 36px; height: 36px; border-radius: 50%;
  border: 2px solid #e2e8f0; background: #fff;
  font-size: 1rem; font-weight: 800; color: #0f172a;
  cursor: pointer; transition: all 0.15s;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.lyn-hdr-btn:hover { border-color: #0f172a; transform: translateY(-1px); }
.lyn-hdr-title {
  flex: 1; text-align: center;
  font-family: 'Fredoka One', cursive; font-size: 1rem; color: #0f172a;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.lyn-voice-btn {
  width: 36px; height: 36px; border-radius: 50%;
  border: 2px solid #e2e8f0; background: #fff;
  font-size: 1rem; cursor: pointer; transition: all 0.15s;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.lyn-voice-btn-on  { border-color: #86efac; background: #f0fdf4; }
.lyn-voice-btn-off { border-color: #cbd5e1; background: #f8fafc; opacity: 0.7; }
.lyn-voice-btn:hover { transform: translateY(-1px); }

/* ── Main ── */
.lyn-part-main {
  position: relative; z-index: 1; flex: 1;
  padding: 18px 14px 32px;
  max-width: 720px; margin: 0 auto; width: 100%;
  display: flex; flex-direction: column; align-items: center; gap: 14px;
}

/* ── Progress row ── */
.lyn-progress-row {
  display: flex; align-items: center; gap: 12px;
  background: rgba(255,255,255,0.92);
  border-radius: 100px; padding: 6px 14px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}
.lyn-progress-count {
  font-family: 'Fredoka One', cursive; font-size: 0.82rem; color: #475569;
}
.lyn-progress-dots { display: flex; gap: 4px; }
.lyn-progress-dot {
  width: 8px; height: 8px; border-radius: 100px; background: #e2e8f0;
  transition: all 0.3s cubic-bezier(.34,1.56,.64,1);
}
.lyn-progress-dot-done { background: #34d399; }
.lyn-progress-dot-cur  { width: 18px; background: #3b82f6; }

/* ── Prompt card ── */
.lyn-prompt-card {
  width: 100%; max-width: 520px;
  display: flex; flex-direction: column; align-items: center; gap: 14px;
  padding: 18px 18px 16px;
  background: rgba(255,255,255,0.94);
  border-radius: 24px;
  border: 1.5px solid rgba(255,255,255,0.92);
  box-shadow: 0 8px 28px rgba(0,0,0,0.06);
}
.lyn-prompt-label {
  font-size: 0.65rem; font-weight: 900; letter-spacing: 0.2em;
  text-transform: uppercase; color: #94a3b8;
}
.lyn-squares-row {
  display: flex; gap: 10px; flex-wrap: wrap; justify-content: center;
}
.lyn-square {
  position: relative;
  width: 76px; height: 84px; border-radius: 18px;
  border: 3px solid;
  display: flex; align-items: center; justify-content: center;
  font-family: 'Fredoka One', cursive;
  cursor: pointer; transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s;
  padding: 0;
}
.lyn-square:hover { transform: translateY(-2px); }
.lyn-square-letter { font-size: 2.4rem; line-height: 1; }
.lyn-square-current { transform: translateY(-3px); }
.lyn-square-upcoming { opacity: 0.78; }
.lyn-square-done .lyn-square-letter { font-size: 2rem; }
.lyn-square-shake { animation: lynSquareShake 0.3s ease; }

.lyn-square-ring {
  position: absolute; inset: -8px; border-radius: 22px;
  border: 4px solid currentColor; opacity: 0.5;
  pointer-events: none;
  animation: lynSquareRingPulse 1.6s ease-out infinite;
}

.lyn-hear-btn {
  background: #f1f5f9; border: 2px solid #e2e8f0;
  border-radius: 100px; padding: 6px 18px;
  font-family: 'Nunito', sans-serif; font-weight: 800; font-size: 0.82rem;
  color: #475569; cursor: pointer;
  transition: all 0.15s;
}
.lyn-hear-btn:hover { border-color: #94a3b8; background: #e2e8f0; }
.lyn-hear-btn:active { transform: scale(0.96); }

/* ── Mic ── */
.lyn-mic-row { display: flex; justify-content: center; }
.lyn-mic-start {
  padding: 11px 28px; border-radius: 100px;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: #fff; border: none; cursor: pointer;
  font-family: 'Fredoka One', cursive; font-size: 0.98rem;
  box-shadow: 0 6px 0 #15803d, 0 8px 18px rgba(34,197,94,0.32);
  transition: transform 0.15s, box-shadow 0.15s;
}
.lyn-mic-start:hover  { transform: translateY(-1px); }
.lyn-mic-start:active { transform: translateY(2px); box-shadow: 0 3px 0 #15803d; }

.lyn-mic-toggle {
  padding: 8px 22px; border-radius: 100px;
  background: rgba(255,255,255,0.9); border: 2px solid #e2e8f0;
  color: #64748b; font-weight: 800; font-size: 0.82rem; cursor: pointer;
  transition: all 0.2s;
}
.lyn-mic-toggle-on { background: #ecfdf5; border-color: #86efac; color: #166534; }

.lyn-or-label {
  font-size: 0.62rem; font-weight: 800; text-transform: uppercase;
  letter-spacing: 0.18em; color: #cbd5e1;
}

/* ── Piano ── */
.lyn-piano-wrap {
  position: relative; display: flex; justify-content: center;
  width: 100%; max-width: 720px;
  overflow: visible;
  background: rgba(255,255,255,0.80); border-radius: 24px;
  padding: 60px 16px 20px;
  box-shadow: 0 14px 45px rgba(0,0,0,0.07), inset 0 1px 0 rgba(255,255,255,0.9);
  border: 1.5px solid rgba(255,255,255,0.9);
}
@media (min-width: 1024px) {
  .lyn-piano-wrap { padding: 60px 28px 24px; }
}

/* ── Utility row ── */
.lyn-utility-row {
  display: flex; align-items: center; justify-content: center;
  gap: 12px; margin-top: 6px;
}
.lyn-utility-link {
  background: none; border: none; cursor: pointer;
  font-weight: 800; font-size: 0.72rem; color: #94a3b8;
  text-decoration: underline; text-decoration-style: dashed;
  text-underline-offset: 3px;
}
.lyn-utility-link:hover { color: #475569; }
.lyn-utility-sep { color: #cbd5e1; font-weight: 800; font-size: 0.72rem; }

/* ── Stop-listening sticky ── */
.lyn-stop-listen-wrap {
  position: fixed; left: 50%; transform: translateX(-50%);
  bottom: calc(10px + env(safe-area-inset-bottom, 0px));
  z-index: 9998; width: 100%; max-width: 360px; padding: 0 14px;
}
.lyn-stop-listen-btn {
  width: 100%; padding: 13px; border-radius: 100px;
  background: #1e293b; color: #fff; border: none; cursor: pointer;
  font-family: 'Fredoka One', cursive; font-size: 0.95rem;
  box-shadow: 0 8px 26px rgba(0,0,0,0.30);
}

/* ── Toast ── */
.lyn-toast {
  position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
  z-index: 9999; background: #1e293b; color: #fff;
  padding: 7px 20px; border-radius: 100px;
  font-weight: 700; font-size: 0.75rem;
  box-shadow: 0 5px 20px rgba(0,0,0,0.26);
}

/* ── Intro video overlay ── */
.lyn-video-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.78);
  backdrop-filter: blur(8px); z-index: 10000;
  display: flex; align-items: center; justify-content: center; padding: 16px;
}
.lyn-video-modal {
  background: #fff; border-radius: 24px; padding: 18px;
  max-width: 720px; width: 100%; box-shadow: 0 20px 60px rgba(0,0,0,0.4);
  position: relative;
}
.lyn-video-close {
  position: absolute; top: 10px; right: 14px;
  background: none; border: none; font-size: 1.4rem;
  color: #94a3b8; cursor: pointer;
}
.lyn-video-title {
  font-family: 'Fredoka One', cursive; font-size: 1.2rem;
  color: #1e293b; text-align: center; margin-bottom: 10px;
}
.lyn-video-frame { width: 100%; aspect-ratio: 16 / 9; background: #000; border-radius: 14px; overflow: hidden; }
.lyn-video-frame video { width: 100%; height: 100%; object-fit: contain; background: #000; display: block; }
.lyn-video-missing { padding: 30px 20px; text-align: center; color: #64748b; font-weight: 700; font-size: 0.88rem; }
.lyn-video-cta-row { display: flex; justify-content: center; margin-top: 14px; }
.lyn-video-cta {
  padding: 10px 24px; border-radius: 100px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: #fff; border: none; cursor: pointer;
  font-family: 'Fredoka One', cursive; font-size: 0.9rem;
  box-shadow: 0 5px 0 #1e40af, 0 7px 16px rgba(59,130,246,0.32);
}

/* ── Finish modal ── */
.lyn-modal-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(0,0,0,0.55); backdrop-filter: blur(6px);
  display: flex; align-items: center; justify-content: center; padding: 16px;
}
.lyn-modal {
  background: #fff; border-radius: 34px; padding: 36px 24px 28px;
  max-width: 440px; width: 100%; box-shadow: 0 28px 70px rgba(0,0,0,0.26);
  text-align: center; position: relative;
  animation: lynBubblePop .4s cubic-bezier(.34,1.56,.64,1);
}
.lyn-modal-close {
  position: absolute; top: 14px; right: 16px;
  background: none; border: none; font-size: 1rem; color: #cbd5e1; cursor: pointer;
}
.lyn-modal-emoji { font-size: 3rem; margin-bottom: 4px; }
.lyn-modal-title { font-family: 'Fredoka One', cursive; font-size: 1.8rem; color: #1e293b; }
.lyn-modal-sub  { font-weight: 700; color: #64748b; margin-top: 5px; font-size: 0.88rem; }
.lyn-modal-journey {
  display: flex; gap: 6px; justify-content: center;
  margin: 16px 0 18px; font-size: 0.72rem; font-weight: 800;
}
.lyn-modal-journey-pill {
  padding: 5px 10px; border-radius: 100px;
  background: #f1f5f9; color: #94a3b8;
}
.lyn-modal-journey-pill-done {
  background: #d1fae5; color: #065f46;
}
.lyn-modal-journey-pill-active {
  background: #ede9fe; color: #5b21b6; box-shadow: 0 0 0 2px #c4b5fd;
}
.lyn-modal-actions { display: flex; flex-direction: column; gap: 10px; }
.lyn-modal-primary {
  padding: 14px 24px; border-radius: 100px;
  background: linear-gradient(135deg, #7c3aed, #6d28d9);
  color: #fff; font-family: 'Fredoka One', cursive; font-size: 1rem;
  border: none; cursor: pointer;
  box-shadow: 0 7px 0 #5b21b6, 0 9px 22px rgba(109,40,217,0.32);
}
.lyn-modal-next {
  padding: 14px 24px; border-radius: 100px;
  background: linear-gradient(135deg, #059669, #047857);
  color: #fff; font-family: 'Fredoka One', cursive; font-size: 1rem;
  border: none; cursor: pointer;
  box-shadow: 0 7px 0 #065f46, 0 9px 22px rgba(5,150,105,0.32);
}
.lyn-modal-secondary-row { display: flex; gap: 9px; }
.lyn-modal-secondary {
  flex: 1; padding: 11px; border-radius: 100px;
  background: #fff; border: 2px solid #e2e8f0;
  font-weight: 800; font-size: 0.82rem; cursor: pointer; color: #334155;
}
.lyn-modal-tertiary {
  flex: 1; padding: 11px; border-radius: 100px;
  background: #f0f9ff; border: 2px solid #bae6fd;
  font-weight: 800; font-size: 0.82rem; cursor: pointer; color: #0369a1;
}

/* ── Small screens — keep squares + piano usable ── */
@media (max-width: 480px) {
  .lyn-square { width: 60px; height: 68px; border-radius: 14px; border-width: 2.5px; }
  .lyn-square-letter { font-size: 1.9rem; }
  .lyn-square-done .lyn-square-letter { font-size: 1.6rem; }
  .lyn-squares-row { gap: 6px; }
  .lyn-prompt-card { padding: 14px 12px 12px; }
  .lyn-progress-count { font-size: 0.74rem; }
  .lyn-piano-wrap { padding: 56px 8px 14px; border-radius: 18px; }
  .lyn-utility-row { gap: 8px; }
}
@media (max-width: 360px) {
  .lyn-square { width: 52px; height: 60px; }
  .lyn-square-letter { font-size: 1.65rem; }
  .lyn-hdr-title { font-size: 0.88rem; }
}
`;